import { Link } from 'react-router-dom';
import { formatBRDateTime } from '@control-center/shared';
import type { DlqItem } from '@control-center/shared';
import { RotateCw } from 'lucide-react';

import { cn } from '@control-center/ui';

interface DlqListProps {
  items: DlqItem[];
  canRetry: boolean;
  onRetry: (item: DlqItem) => void;
  retryingId: string | null;
}

function ChannelBadge({ channel }: { channel: string }) {
  const isEmail = channel === 'email';
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-[6px] px-2 py-[2px] text-[11px] font-medium',
        isEmail ? 'bg-brand/15 text-brand' : 'bg-good/15 text-good',
      )}
    >
      {isEmail ? 'Email' : 'WhatsApp'}
    </span>
  );
}

export function DlqList({ items, canRetry, onRetry, retryingId }: DlqListProps): JSX.Element {
  return (
    <div className="overflow-hidden rounded-[12px] border border-line bg-panel">
      <table className="w-full text-sm">
        <thead className="border-b border-line bg-panel-2 text-left text-[11px] uppercase tracking-wider text-muted-3">
          <tr>
            <th className="px-4 py-3 font-medium">Cliente</th>
            <th className="px-4 py-3 font-medium">Canal</th>
            <th className="px-4 py-3 font-medium">Template</th>
            <th className="px-4 py-3 font-medium text-right">Tentativas</th>
            <th className="px-4 py-3 font-medium">Erro</th>
            <th className="px-4 py-3 font-medium">Falhou em</th>
            <th className="px-4 py-3 font-medium text-right">Ação</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-line">
          {items.map((item) => (
            <tr key={item.id} className="text-ink hover:bg-panel-2">
              <td className="px-4 py-3">
                <Link
                  to={`/identities/${item.clientId}`}
                  className="text-ink underline-offset-2 hover:underline"
                >
                  {item.clientName}
                </Link>
              </td>
              <td className="px-4 py-3">
                <ChannelBadge channel={item.channel} />
              </td>
              <td className="px-4 py-3 font-mono text-[12px] text-muted">
                {item.templateKey ?? '—'}
              </td>
              <td className="px-4 py-3 text-right font-mono tnum text-muted">
                {item.attemptsCount}
              </td>
              <td
                className="max-w-[280px] truncate px-4 py-3 text-muted-2"
                title={item.errorMessage ?? ''}
              >
                {item.errorMessage ?? '—'}
              </td>
              <td className="px-4 py-3 text-muted-2 tnum">
                {item.failedAt ? formatBRDateTime(item.failedAt) : '—'}
              </td>
              <td className="px-4 py-3">
                <div className="flex items-center justify-end gap-1">
                  {canRetry && (
                    <button
                      type="button"
                      onClick={() => onRetry(item)}
                      disabled={retryingId === item.id}
                      className="inline-flex h-7 items-center gap-1 rounded-[6px] border border-line bg-panel-2 px-2 text-[12px] text-ink hover:bg-panel-3 disabled:cursor-not-allowed disabled:opacity-50"
                      aria-label={`Reprocessar ${item.id}`}
                    >
                      <RotateCw size={13} strokeWidth={1.8} />
                      {retryingId === item.id ? 'Reenfileirando…' : 'Reprocessar'}
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
